import { useRouter } from 'next/router'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { type SanityCombinedListingProductFragment } from '@data/sanity/queries/types/product'
import { getSanityImageUrl } from '@lib/image'
import { LanguageContext } from '@lib/language-context'
import { colorOptionNames } from '@lib/product/product'
import { ProductCombinedListingContext } from '@lib/product/combined-listing/context'
import { getIsEmptyFrame } from '@lib/product/combined-listing/helpers'
import { useFrameSettings } from '@lib/product/combined-listing/frame'
import { useCombinedListingProductPrice } from '@lib/product/combined-listing/price'
import { getCombinedListingProductVariantSku } from '@lib/product/combined-listing/sku'
import { type CombinedListingParameterMap } from '@lib/product/combined-listing/types'
import { ProductContext } from '@lib/product/context'
import { parseOptionalParameter } from '@lib/request'
import { getPageUrl } from '@lib/routes'
import { StringsContext } from '@lib/strings-context'
import { UserContext } from '@lib/user/context'
import { getHasUserPurchasePermission } from '@lib/user/helpers'

import CombinedListingFrameDimensions from '@blocks/product/combined-listing-frame-dimensions'
import CombinedListingFramePreviewSettings from '@blocks/product/combined-listing-frame-preview-settings'
import CombinedListingProductActions from '@blocks/product/combined-listing-product-actions'
import CombinedListingProductSelect from '@blocks/product/combined-listing-product-select'
import ProductActionsGuest from '@blocks/product/product-actions-guest'
import ProductDescription from '@blocks/product/product-description'
import ProductGallery from '@blocks/product/product-gallery'
import ProductPrice from '@blocks/product/product-price'

const CombinedListingProductHero = () => {
  const { locale } = useContext(LanguageContext)
  const {
    combinedListingSettings,
    combinedListingConfiguration,
    combinedListingArtworkSize,
    combinedListingProducts,
    combinedListingVariants,
    combinedListingFrameSize,
    setCombinedListingStateManagerType,
    setCombinedListingSettings,
    setCombinedListingConfiguration,
    setCombinedListingMainProductId,
    setCombinedListingFrameSize,
    setCombinedListingArtworkSize,
    clearCombinedListingProductType,
  } = useContext(ProductCombinedListingContext)
  const { product } = useContext(ProductContext)
  const strings = useContext(StringsContext)
  const { user } = useContext(UserContext)

  const router = useRouter()

  const [artworkUrl, setArtworkUrl] = useState<string | null>(null)
  const [backgroundColor, setBackgroundColor] = useState('#f7f7f7')

  const productGalleryWrapperRef = useRef<HTMLDivElement | null>(null)

  const frameProduct = useMemo(
    () =>
      product?.productCombinedListing?.frame?.find(
        (frameProduct) => frameProduct._id === product._id,
      ),
    [product],
  )

  const frameColor = useMemo(() => {
    // Get frame color from product options
    const colorOption = product?.options?.find((option) =>
      colorOptionNames.includes(option.name),
    )

    if (colorOption) {
      return colorOption.values[0]
    }

    // Get frame color from frame builder texture title
    if (frameProduct) {
      return frameProduct.combinedListingFrameBuilderProfile?.texture?.title
    }
  }, [frameProduct, product])

  const combinedListingProductIds = useMemo(
    () =>
      combinedListingProducts?.map(
        (combinedListingActiveProduct) =>
          combinedListingActiveProduct.productID,
      ) ?? [],
    [combinedListingProducts],
  )

  const combinedListingVariantIds = useMemo(
    () =>
      combinedListingVariants?.map(
        (combinedListingActiveVariant) =>
          combinedListingActiveVariant.variantID,
      ) ?? [],
    [combinedListingVariants],
  )

  const [price, comparePrice] = useCombinedListingProductPrice(
    combinedListingProducts,
    combinedListingVariants,
    combinedListingConfiguration,
  )

  const productPageUrl = useMemo(() => {
    if (!product) {
      return
    }

    const combinedListingUrlParameters: CombinedListingParameterMap = {
      products: parseOptionalParameter(router.query.products) ?? null,
      frame_size: parseOptionalParameter(router.query.frame_size) ?? null,
      artwork_size: parseOptionalParameter(router.query.artwork_size) ?? null,
    }

    return getPageUrl({
      locale,
      pageType: 'product',
      slugs: product.slug.current,
      parameters: combinedListingUrlParameters,
    })
  }, [locale, product, router.query])

  const sku = useMemo(() => {
    if (!product || !combinedListingFrameSize) {
      return
    }

    // Product SKU (product ID with size)
    if (
      !combinedListingSettings ||
      !combinedListingConfiguration ||
      combinedListingProductIds.length === 0
    ) {
      return product.sku
    }

    // Generated SKU (product ID with size and extra product IDs)
    return getCombinedListingProductVariantSku(
      combinedListingSettings,
      combinedListingConfiguration,
      combinedListingProductIds,
      combinedListingFrameSize,
      combinedListingArtworkSize ?? undefined,
    )
  }, [
    combinedListingFrameSize,
    combinedListingConfiguration,
    combinedListingArtworkSize,
    combinedListingProductIds,
    combinedListingSettings,
    product,
  ])

  const isEmptyFrame = useMemo(() => {
    return getIsEmptyFrame(combinedListingProducts ?? [])
  }, [combinedListingProducts])

  const defaultArtworkUrl = useMemo(() => {
    if (!product?.frameBuilderDefaultFrame?.previewImage) {
      return null
    }

    return (
      getSanityImageUrl(product.frameBuilderDefaultFrame.previewImage) ?? null
    )
  }, [product])

  const predefinedBackgroundColors = useMemo(() => {
    if (!product?.frameBuilderDefaultFrame?.backgroundColorPresets) {
      return []
    }

    return product.frameBuilderDefaultFrame.backgroundColorPresets.map(
      (backgroundColorPreset) => backgroundColorPreset.hex,
    )
  }, [product])

  const handleMainProductChange = useCallback(
    (product: SanityCombinedListingProductFragment) => {
      // Update combined listing state
      clearCombinedListingProductType('glass')
      clearCombinedListingProductType('backboard')
      clearCombinedListingProductType('passepartout')
      clearCombinedListingProductType('spacer')
      setCombinedListingFrameSize(null)
      setCombinedListingArtworkSize(null)

      // Update page URL
      const combinedListingUrlParameters: CombinedListingParameterMap = {
        products: `${product.productID}`,
        frame_size: null,
        artwork_size: null,
      }
      const newProductPageUrl = getPageUrl({
        locale,
        pageType: 'product',
        slugs: product.slug.current,
        parameters: combinedListingUrlParameters,
      })
      router.replace(newProductPageUrl)
    },
    [
      clearCombinedListingProductType,
      locale,
      router,
      setCombinedListingArtworkSize,
      setCombinedListingFrameSize,
    ],
  )

  // Load combined listing configuration
  useEffect(() => {
    if (product) {
      setCombinedListingStateManagerType('url')
      setCombinedListingMainProductId(product.productID)
      setCombinedListingConfiguration(product.productCombinedListing)
      setCombinedListingSettings(product.productCombinedListingSettings)
    }

    return () => {
      setCombinedListingStateManagerType('memory')
      setCombinedListingConfiguration()
      setCombinedListingSettings()
    }
  }, [
    product,
    setCombinedListingConfiguration,
    setCombinedListingMainProductId,
    setCombinedListingSettings,
    setCombinedListingStateManagerType,
  ])

  const frameSettings = useFrameSettings(
    artworkUrl ?? defaultArtworkUrl,
    backgroundColor,
  )

  if (!product) {
    return null
  }

  const hasUserPurchasePermission = getHasUserPurchasePermission(user)
  const hasActions = !!user && hasUserPurchasePermission
  const hasGuestActions = !!user && !hasUserPurchasePermission

  return (
    <section className="container grid grid-cols-1 md:grid-cols-2 gap-8 py-12">
      <div ref={productGalleryWrapperRef}>
        <ProductGallery
          photosets={product.galleryPhotos ?? []}
          frameSettings={frameSettings}
          preloadFirstImage
          hasDrag
          hasThumbnails
        />
      </div>

      <div className="flex flex-col w-full md:max-w-[620px] md:ml-auto gap-y-[1.875rem]">
        <div className="space-y-2.5">
          <div>
            <span className="text-sm">{product.combinedListingSku}</span>
            <h1 className="is-h2">{product.title}</h1>
          </div>

          {hasUserPurchasePermission && (
            <ProductPrice
              size="lg"
              price={price}
              comparePrice={comparePrice}
              showPrice
              showPriceTaxSuffix
              showCurrency
            />
          )}

          {!!sku && (
            <div className="text-sm">
              {strings.productSkuLabel} {sku}
            </div>
          )}
        </div>

        <CombinedListingProductSelect
          onMainProductChange={handleMainProductChange}
          frameColor={frameColor}
        />

        {!!frameSettings && (
          <CombinedListingFramePreviewSettings
            predefinedBackgroundColors={predefinedBackgroundColors}
            defaultBackgroundColor={backgroundColor}
            onFileChange={setArtworkUrl}
            onBackgroundColorChange={setBackgroundColor}
          />
        )}

        {hasActions && (
          <CombinedListingProductActions
            product={product}
            productIds={combinedListingProductIds}
            variantIds={combinedListingVariantIds}
            frameSize={combinedListingFrameSize ?? ''}
            artworkSize={combinedListingArtworkSize ?? undefined}
            isEmptyFrame={isEmptyFrame}
          />
        )}

        {hasGuestActions && <ProductActionsGuest returnTo={productPageUrl} />}

        <div className="max-w-sm">
          <ProductDescription
            title={strings.productDescriptionHeading}
            description={product?.description}
            isOpenByDefault
          />
          <CombinedListingFrameDimensions />
        </div>
      </div>
    </section>
  )
}

export default CombinedListingProductHero
